import type { PromptTemplate } from "@/api/prompts";

const KIND_ORDER: Record<string, number> = {
  "job-research-light": 0,
  "job-research": 1,
  "job-research-deep": 2,
};

export function sortPromptTemplates(templates: PromptTemplate[]): PromptTemplate[] {
  return [...templates].sort((a, b) => {
    const orderA = KIND_ORDER[a.kind] ?? 99;
    const orderB = KIND_ORDER[b.kind] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `~${seconds}s`;
  return `~${Math.round(seconds / 60)} min`;
}

interface PromptTemplatePickerProps {
  templates: PromptTemplate[];
  selectedKind: string;
  onSelect: (kind: string) => void;
  onViewPrompt?: (kind: string) => void;
  disabled?: boolean;
  emptyMessage?: string;
  preserveOrder?: boolean;
}

export function PromptTemplatePicker({
  templates,
  selectedKind,
  onSelect,
  onViewPrompt,
  disabled,
  emptyMessage = "No templates available. Add prompts with the job-research category in Settings → Prompts.",
  preserveOrder = false,
}: PromptTemplatePickerProps) {
  const sorted = preserveOrder ? templates : sortPromptTemplates(templates);

  if (sorted.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="space-y-2" role="radiogroup" aria-label="Research template">
      {sorted.map((template) => {
        const isSelected = selectedKind === template.kind;
        return (
          <label
            key={template.kind}
            className={`flex cursor-pointer gap-3 rounded-lg border p-4 transition ${
              isSelected
                ? "border-brand bg-brand/5 ring-1 ring-brand/30"
                : "border-border bg-card hover:border-muted-foreground/30 hover:bg-muted/10"
            } ${disabled ? "pointer-events-none opacity-50" : ""}`}
          >
            <input
              type="radio"
              name="research-template"
              value={template.kind}
              checked={isSelected}
              onChange={() => onSelect(template.kind)}
              disabled={disabled}
              className="mt-1 h-4 w-4 shrink-0 accent-[hsl(26_73%_51%)]"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">{template.name}</span>
                <span className="rounded-full bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {formatDuration(template.maxDuration)}
                </span>
                {template.requiresProfile && (
                  <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                    Profile
                  </span>
                )}
                {template.requiresCv && (
                  <span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-600 dark:text-violet-400">
                    CV
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{template.description}</p>
              {onViewPrompt && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onViewPrompt(template.kind);
                  }}
                  className="mt-2 text-xs font-medium text-brand hover:underline"
                >
                  View prompt
                </button>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
}
